import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../ui/sidebar';
import { Link } from '../ui/link';
import { ArrowLeft, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '../../lib/utils';
import { UserAvatarMenu } from '../UserAvatarMenu';
import { OfflineIndicator } from '../OfflineIndicator';
import { ThemeToggle } from '../ThemeToggle';
import { APP_SIDEBAR_HEADER_CLASSNAME, APP_SIDEBAR_HEADER_STYLE } from '../../constants/layout';
import logoUrl from '../../assets/logos/SVG/POSITIVO/logo scribane_positivo reducido h adjusted.svg?url';
import logoNegativoUrl from '../../assets/logos/SVG/BLANCO/logo scribane_blanco reducido h adjusted.svg?url';
import { ADMIN_NAV_ITEMS, isAdminNavActive } from './admin-nav';

export const AdminSidebar: React.FC = () => {
  const { t } = useLocale();
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const { state, isMobile, openMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const isCollapsed = isMobile ? !openMobile : state === 'collapsed';

  const handleMenuClick = (): void => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar collapsible="icon" className="bg-sidebar text-sidebar-foreground">
      <SidebarHeader className={APP_SIDEBAR_HEADER_CLASSNAME} style={APP_SIDEBAR_HEADER_STYLE}>
        <SidebarMenu className="h-full">
          <SidebarMenuItem className={cn('h-full', isCollapsed && 'flex items-center justify-center')}>
            {isCollapsed ? (
              <SidebarMenuButton
                tooltip="Toggle sidebar"
                className="h-[38px] !justify-center !text-center"
                onClick={toggleSidebar}
                aria-label="Toggle Sidebar"
              >
                <PanelLeftOpen className="!h-6 !w-6 text-primary" />
                <span className="sr-only">{t('toggle_sidebar')}</span>
              </SidebarMenuButton>
            ) : (
              <div className="flex h-full w-full items-center gap-1">
                <SidebarMenuButton asChild tooltip="Scribane" className="h-full flex-1 py-0 pl-2">
                  <Link to="/admin" onClick={handleMenuClick} className="flex h-full w-full items-center py-0">
                    <img
                      src={resolvedTheme === 'dark' ? logoNegativoUrl : logoUrl}
                      alt="Scribane"
                      className="h-[1.8rem] w-auto min-w-[150px]"
                    />
                  </Link>
                </SidebarMenuButton>
                <SidebarMenuButton
                  tooltip="Toggle sidebar"
                  className="h-full !justify-center !text-center !p-1"
                  onClick={toggleSidebar}
                  aria-label="Toggle Sidebar"
                >
                  <PanelLeftClose className="!h-6 !w-6 text-primary" />
                  <span className="sr-only">{t('toggle_sidebar')}</span>
                </SidebarMenuButton>
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip={t('back_to_desk')}
                  className={cn('h-[38px] py-0', isCollapsed && '!justify-center !text-center !gap-0')}
                >
                  <Link
                    to="/desk"
                    onClick={handleMenuClick}
                    className={cn(isCollapsed && 'flex h-full w-full items-center justify-center')}
                  >
                    {isCollapsed ? (
                      <ArrowLeft className="!h-6 !w-6 text-primary" />
                    ) : (
                      <>
                        <ArrowLeft className="!h-4 !w-4 text-secondary-foreground" />
                        <span className="text-primary">{t('back_to_desk')}</span>
                      </>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {ADMIN_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const isActive = isAdminNavActive(location.pathname, item);
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={t(item.labelKey)}
                      className={cn('h-[38px] py-0', isCollapsed && '!justify-center !text-center !gap-0')}
                    >
                      <Link
                        to={item.to}
                        onClick={handleMenuClick}
                        className={cn(isCollapsed && 'flex h-full w-full items-center justify-center')}
                      >
                        {isCollapsed ? (
                          <Icon className="!h-6 !w-6 text-primary" />
                        ) : (
                          <>
                            <Icon className="!h-4 !w-4 text-secondary-foreground" />
                            <span className="text-primary">{t(item.labelKey)}</span>
                          </>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {!isCollapsed && !isMobile && (
                <SidebarMenuItem>
                  <OfflineIndicator variant="sidebar" isCollapsed={isCollapsed} />
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <ThemeToggle collapsed={isCollapsed} />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <UserAvatarMenu />
    </Sidebar>
  );
};
